import React, { useState } from "react";
import { Alert, Badge, Button, Card, Icon } from "stories/Admin";
import {
  formatDateTime,
  formatDay,
  formatNumber,
  isoDayFromToday,
  keyStatus,
  PartnerKey,
} from "./partnerTypes";
import styles from "./PartnerPage.module.scss";

interface PartnerKeyCardProps {
  apiKey: PartnerKey;
  /** Superadmin ser også avviste domener og sjekkhistorikk. */
  superadmin?: boolean;
  checking?: boolean;
  onCheck: (k: PartnerKey) => void;
  onEdit?: (k: PartnerKey) => void;
  onRevoke: (k: PartnerKey) => void;
  onShowChecks?: (k: PartnerKey) => void;
  /** Superadmin: aktiver en stoppet nøkkel og sett siste dag den virker selv om sjekken feiler. */
  onActivate?: (k: PartnerKey) => void;
}

/** Én widget-nøkkel: status for kreditering, innlimingskode, forhåndsvisning og bruk siste 30 dager. */
const PartnerKeyCard: React.FC<PartnerKeyCardProps> = ({
  apiKey: k,
  superadmin,
  checking,
  onCheck,
  onEdit,
  onRevoke,
  onShowChecks,
  onActivate,
}) => {
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(false);
  const status = keyStatus(k);
  const max = Math.max(1, ...k.usage.days.map((d) => d.views + d.blocked));
  const revoked = !!k.revokedAt;
  const graceActive = !!k.graceUntil && k.graceUntil >= isoDayFromToday(0);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(k.embedCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card
      className={revoked ? styles.dim : undefined}
      title={
        <span className={styles.keyTitle}>
          {k.name}
          <Badge tone={status.tone}>{status.label}</Badge>
        </span>
      }
      subtitle={
        <>
          <a href={k.pageUrl} target="_blank" rel="noopener noreferrer">
            {k.pageUrl}
          </a>{" "}
          · låst til <b>{k.domain}</b> · laget {formatDateTime(k.createdAt)}
          {k.createdByEmail ? ` av ${k.createdByEmail}` : ""}
          {revoked && <> · trukket tilbake {formatDateTime(k.revokedAt)}</>}
        </>
      }
      actions={
        !revoked && (
          <div className={styles.actions}>
            {onActivate && (k.pausedAt || graceActive) && (
              <Button
                size="sm"
                variant={k.pausedAt ? "primary" : "secondary"}
                icon={<Icon name="check" size={14} />}
                onClick={() => onActivate(k)}
              >
                {k.pausedAt ? "Aktiver" : "Endre frist"}
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              icon={<Icon name="swap" size={14} />}
              loading={checking}
              onClick={() => onCheck(k)}
            >
              Sjekk nå
            </Button>
            {onEdit && (
              <Button
                size="sm"
                variant="secondary"
                icon={<Icon name="edit" size={14} />}
                onClick={() => onEdit(k)}
              >
                Endre side
              </Button>
            )}
            {onShowChecks && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onShowChecks(k)}
              >
                Sjekkhistorikk
              </Button>
            )}
            <Button size="sm" variant="danger" onClick={() => onRevoke(k)}>
              Trekk tilbake
            </Button>
          </div>
        )
      }
    >
      {!revoked && (
        <>
          {k.pausedAt ? (
            <Alert tone="error">
              Widgeten er stoppet siden {formatDateTime(k.pausedAt)} og viser
              ingen tider. {k.creditMessage} Rett opp siden og trykk «Sjekk nå».
            </Alert>
          ) : graceActive && k.creditStatus !== "OK" ? (
            <Alert tone="warn">
              Aktivert til og med {formatDay(k.graceUntil)}. Widgeten virker til
              da selv om sjekken feiler, og stoppes etter det hvis siden ikke
              krediterer iBønn.
              {k.creditMessage && k.creditCheckedAt && (
                <>
                  {" "}
                  Siste sjekk ({formatDateTime(k.creditCheckedAt)}):{" "}
                  {k.creditMessage}
                </>
              )}
            </Alert>
          ) : k.creditStatus === "OK" ? (
            <Alert tone="ok">
              Sist sjekket {formatDateTime(k.creditCheckedAt)}: siden krediterer
              iBønn.
            </Alert>
          ) : k.creditMessage ? (
            <Alert tone="warn">
              {k.creditMessage}
              {k.creditCheckedAt && (
                <> (sjekket {formatDateTime(k.creditCheckedAt)})</>
              )}
            </Alert>
          ) : (
            <Alert tone="info">
              Lim inn koden under på siden, og trykk «Sjekk nå».
              {k.creditDeadline && (
                <>
                  {" "}
                  Widgeten må være på plass med lenke til iBønn innen{" "}
                  {formatDateTime(k.creditDeadline)}, ellers stoppes den.
                </>
              )}
            </Alert>
          )}

          <div className={styles.embed}>
            <div className={styles.embedHead}>
              <b>Kode til nettsiden</b>
              <Button
                size="sm"
                icon={<Icon name={copied ? "check" : "copy"} size={14} />}
                onClick={copy}
              >
                {copied ? "Kopiert" : "Kopier"}
              </Button>
            </div>
            <pre className={styles.code}>{k.embedCode}</pre>
            <span className={styles.muted}>
              Både widgeten og lenken til iBønn må stå i HTML-en på {k.pageUrl}.
              Sjekken kjører hver natt. Mangler noe, stopper widgeten med en
              gang.
            </span>
          </div>

          <div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPreview((p) => !p)}
            >
              {preview ? "Skjul forhåndsvisning" : "Vis forhåndsvisning"}
            </Button>
            {preview && (
              <iframe
                className={styles.preview}
                src={k.embedUrl}
                title={`Forhåndsvisning av ${k.name}`}
                loading="lazy"
              />
            )}
          </div>
        </>
      )}

      <div className={styles.usageRow}>
        {(
          [
            ["I dag", k.usage.today],
            ["Siste 7 dager", k.usage.last7Days],
            ["Siste 30 dager", k.usage.last30Days],
            ["Totalt", k.usageCount],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className={styles.stat}>
            <b>{formatNumber(value)}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div
        className={styles.bars}
        role="img"
        aria-label={`Visninger per dag de siste 30 dagene, totalt ${formatNumber(k.usage.last30Days)}`}
      >
        {k.usage.days.map((d) => (
          <span
            key={d.day}
            className={styles.bar}
            title={`${new Date(d.day).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}: ${formatNumber(d.views)} visninger${d.blocked ? `, ${formatNumber(d.blocked)} avvist` : ""}`}
          >
            <span
              className={styles.barFill}
              style={{ height: `${(d.views / max) * 100}%` }}
            />
            {d.blocked > 0 && (
              <span
                className={styles.barBlocked}
                style={{ height: `${(d.blocked / max) * 100}%` }}
              />
            )}
          </span>
        ))}
      </div>

      <div className={styles.lists}>
        <div>
          <b>Mest valgte moskeer (30 dager)</b>
          {k.usage.topMosques.length === 0 ? (
            <span className={styles.muted}>Ingen ennå</span>
          ) : (
            <ol className={styles.list}>
              {k.usage.topMosques.map((m) => (
                <li key={m.name}>
                  {m.name}{" "}
                  <span className={styles.muted}>{formatNumber(m.views)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        {superadmin && (
          <div>
            <b>Vist fra (30 dager)</b>
            {k.usage.origins.length === 0 ? (
              <span className={styles.muted}>Ingen ennå</span>
            ) : (
              <ol className={styles.list}>
                {k.usage.origins.map((o) => (
                  <li key={o.name}>
                    {o.name}{" "}
                    <span className={styles.muted}>
                      {formatNumber(o.views)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
            {k.usage.blocked30Days > 0 && (
              <span className={styles.muted}>
                {formatNumber(k.usage.blocked30Days)} visninger avvist (feil
                domene, pause eller for mange kall)
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PartnerKeyCard;
